import React, { useState } from 'react';
import {
  FaChartBar,
  FaInfoCircle,
  FaLightbulb,
  FaVolumeUp,
  FaDownload,
  FaCopy,
  FaClipboardList,
  FaFlask,
  FaBoxes,
  FaDollarSign,
  FaBook
} from 'react-icons/fa';
import './css/style.scss'; 
import config from '../../../configs/config';

const businessSummaryData = [
   {
    title: 'Overall Summary',
    
    content: [
      'In Q4 2024, systemic underperformance was observed across revenue, margins, product channels, and geographies. OTC and Prescription segments—especially in direct sales models—saw sharp downturns. Regions like the UK, USA, India, and Canada faced significant declines driven by demand erosion, pricing inefficiencies, and operational strain. OTC portfolios suffered widespread margin collapses, while Prescription drugs in the USA showed signs of prescriber disengagement. These Q4 trends reveal deep-rooted fragility demanding immediate correction in go-to-market execution and channel strategies.' ]
  },
  {
    title: 'Revenue Drivers & Leakages - Q4 2024 Performance',
    icon: <FaChartBar size={14} />,
    content: [
      'Q4 2024 saw topline pressure across OTC and Prescription segments, with sharp sales drops in both direct and distributor models. HealthTech_Inc posted a 43.17% drop in UK OTC direct sales, BioMed_Solutions recorded a 24.82% fall in Canada’s distributor sales, and TheraPharma_Ltd saw a 43.73% decline in USA Prescription direct sales. These reflect market execution gaps and salesforce challenges.' 
       ]
  },
  {
    title: 'Operational & Business Unit Signals - Q4 2024 Performance:',
    icon: <FaInfoCircle size={14} />,
    content: [
      'Operational metrics in Q4 2024 flagged major cost and margin issues, especially in UK and India OTC units. HealthTech_Inc reported a 75.54% margin collapse, MedChem_Enterprises saw 55.88% erosion, and TheraPharma_Ltd faced dual setbacks: 39.75% UK and 35.2% India margin declines. This underscores structural cost burdens and promotional inefficiencies.'    ]
  },
  {
    title: 'Product & Segment Performance - Q4 2024 Performance:',
    icon: <FaLightbulb size={14} />,
    content: [
      'Across Q4 2024, OTC portfolios in UK, India, and Canada underperformed, impacted by mispricing and poor consumer traction. In the USA, Prescription direct sales struggled—TheraPharma_Ltd being a key case. Direct models were more affected than distributor channels, reflecting fragility in product-channel execution.'    ]
  },
   {
    title: 'Market & Regional Challenges - Q4 2024 Performance:',
    icon: <FaLightbulb size={14} />,
    content: [
     'Q4 2024 exposed broad-based regional issues: the UK saw KPI declines, the USA reported a 43.73% drop in Prescription direct sales, India faced OTC margin pressures, and Canada’s distributor health weakened. These point to rising operational risks and highlight the urgency for targeted regional action plans.'    ]
  }
];

const insightIconMap = {
  Overall: <FaClipboardList size={14} />,
  Reputation: <FaFlask size={14} />,
  'Product Performance': <FaBoxes size={14} />,
  Profitability: <FaDollarSign size={14} />,
  Compliance: <FaBook size={14} />
};

const submitQuery = (query, type, id) => {
  let url =
    (config.prefix ? '/' + config.prefix_name : '') +
    '/chat?query=' +
    query.replaceAll('%', ' percentage') +
    '&type=' +
    type;

  if (id) {
    url += '&id=' + id;
  }
  window.location = url;
};

const keyInsightsData = [
  {
    title: 'UK as the Epicenter of Decline',
    content:
      'OTC sales in the UK dropped 43.17%, with margins eroding up to 75.54%, showing brand erosion, pricing misalignment, and structural cost inefficiencies.',
    prompts: [
      'Give me the sales and margin picture for OTC in the UK and explain the drivers of the drop'
    ]
  },
  {
    title: 'US Prescription Sales Breakdown',
    content:
      'Prescription direct sales in the USA fell 43.73% (TheraPharma Ltd), highlighting serious prescriber disengagement, market access gaps, and front-line weakness in mature therapeutic markets.',
    prompts: [
      'Explain the performance of TheraPharma Ltd in the US prescription segment?'
    ]
  },
  {
    title: 'Widespread Margin Stress Across OTC',
    content:
      'Multiple players (MedChem, TheraPharma, HealthTech) reported margin contractions between 35–75%, signaling critical operational weaknesses in cost control, promotions, and unit-level profitability.',
    prompts: [
      'Show me margin trends for MedChem, TheraPharma, and HealthTech in OTC and explain the weakness'
    ]
  },
  {
    title: 'Canada Distributor Fragility',
    content:
      'Canada distributor sales declined 24.82%, with distributor pullbacks and inventory misalignment, raising concerns on channel health and sell-through capability.',
    prompts: [
      'Give me insights on channel health and sell-through capability in Canada’s OTC market'
    ]
  },
  {
    title: 'India’s OTC Vulnerability',
    content:
      'India’s OTC margins declined 35.2%, driven by high input costs and aggressive promotional spends, exposing fragile resilience in cost and pricing structures.',
    prompts: [
      'How are India’s OTC operations performing on margins? What’s driving the stress?'
    ]
  }
];

const BusinessNarratives = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  
  // Conditionally add the transparent-bg class
  const contentCardClass = `content-card ${selectedTab === 1 ? 'transparent-bg' : ''}`;

  return (
    <div className="business-narratives-container">
      {/* Gradient Header */}
      <div className="header-gradient">
        <h3>Curated key summaries for you!</h3>
        <h4 className="key-title">Key Narratives</h4>

        {/* Tabs */}
        <div className="tabs">
          <button
            className={selectedTab === 0 ? 'active' : ''}
            onClick={() => setSelectedTab(0)}
          >
            Business Summary
          </button>
          <button
            className={selectedTab === 1 ? 'active' : ''}
            onClick={() => setSelectedTab(1)}
          >
            Key Insights
          </button>
        </div>

        {selectedTab === 1 && (
          <div className="insight-note">
            <FaInfoCircle style={{ marginRight: '6px' }} />
            <span>Click on any conversation tags to initiate an analysis</span>
          </div>
        )}
      </div>

      {/* White Card Content */}
      <div className={contentCardClass}>
        {selectedTab === 0 && (
          <>
            {/* Title + Icons Row */}
            <div className="content-header">
              <h4 style={{fontSize:'1rem'}}>Business Performance Summary</h4>
              <div className="icons">
                <FaVolumeUp />
                <FaDownload />
                <FaCopy />
              </div>
            </div>

            <div>
              {businessSummaryData.map((section, index) => (
                <div key={index} className="summary-section">
                  <div className="section-title">
                    {section.icon}
                    <span>{section.title}</span>
                  </div>
                  <p>{section.content}</p>
                </div>
              ))}
            </div>
          </>
        )}

        {selectedTab === 1 && (
          <div className="key-insights-grid">
            {keyInsightsData.map((insight, index) => (
              <div key={index} className="insight-card">
                <div className="section-title">
                  {insightIconMap[insight.title] || <FaChartBar size={14} />}
                  <strong>{insight.title}</strong>
                </div>
                <p>{insight.content}</p>
                <div className="prompts">
                  {insight.prompts.map((prompt, i) => (
                    <button
                      key={i}
                      onClick={() => submitQuery(prompt, 'insight')}
                      className="prompt-btn"
                    >
                      {prompt}
                    </button>
                  ))}
                  <button className="more-btn">+{insight.prompts.length}</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessNarratives;